import config from '@/config';
import { fetchWithKeycloakToken } from '@/features/Keycloak/keycloakService';
import { Device, Media } from '@/generated/graphql';
import { ClientDevice } from '@/models/device.models';

export const mimeToExtension = (contentType: string): string => {
  const mimeToExt = {
    'image/jpeg': 'jpg',
    'image/jpe': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/webp': 'webp',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/ogg': 'ogg',
  };
  return mimeToExt[contentType as keyof typeof mimeToExt] || 'jpg';
};

export async function downloadMedia({
  media,
  device,
}: {
  media?: Media | null;
  device: Device;
}) {
  if (!media) return;
  const mediaEndpointUrl = getMediaEndpointUrl({
    mediaId: media.mediaId!,
    timestamp: media.time,
    getSignedUrl: false,
  });

  const response = await fetchWithKeycloakToken(mediaEndpointUrl.toString());
  const contentType = response.headers.get('Content-Type') || '';
  const mineType = mimeToExtension(contentType);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const aTag = document.createElement('a');
  const filename = `${device.name}_${device.site?.name}_${media ? media.time : new Date().toISOString()}_${media?.kind}.${mineType}`;

  aTag.href = url;
  aTag.download = filename;
  document.body.appendChild(aTag);
  aTag.click();
  document.body.removeChild(aTag);
  URL.revokeObjectURL(url);
}

export function getDeviceMedia(device: ClientDevice): Media | undefined {
  const processedMedia = (device?.lastProcessedMedia ?? [])?.[0];
  const originalMedia = device?.lastMedia?.media as Media;
  return processedMedia ?? originalMedia;
}

export function getMediaEndpointUrl({
  timestamp,
  deviceId,
  mediaId,
  kind,
  getSignedUrl = true,
}: {
  timestamp?: string;
  deviceId?: string;
  mediaId?: string;
  kind?: string;
  getSignedUrl?: boolean;
}): string {
  const endpoint = getSignedUrl ? 'media/signed' : 'media';
  const mediaEndpointUrl = new URL(`${config.rest.url}/${endpoint}`);
  if (deviceId) {
    mediaEndpointUrl.searchParams.set('deviceId', deviceId);
  }
  if (mediaId) {
    mediaEndpointUrl.searchParams.set('id', mediaId);
  }
  if (timestamp) {
    mediaEndpointUrl.searchParams.set('timestamp', timestamp);
  }
  if (kind) {
    mediaEndpointUrl.searchParams.set('kind', kind);
  }
  return mediaEndpointUrl.toString();
}
